import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from '@/components/ui';
import { DataSourceRowGroup } from '@/types/DataSource';
import { describeActionError } from '../../../utils/loadError';

export type DataSourceFieldKey =
  | 'code'
  | 'name'
  | 'type'
  | 'property'
  | 'label'
  | 'sortOrder'
  | 'isActive';

/**
 * How a field is edited.
 *
 * `select` exists for `type` specifically. A datasource only ever reads rows whose type matches
 * one of its question keys, so a typo writes a row that looks saved here and is invisible on
 * every phone - there is no endpoint that would select it. The options come from the server,
 * which derives them from the definitions.
 */
export type DataSourceFieldControl = 'text' | 'number' | 'checkbox' | 'select';

export interface DataSourceFormField {
  key: DataSourceFieldKey;
  label: string;
  required?: boolean;
  control?: DataSourceFieldControl;
  /** Shown under the field. Worth using where a column's meaning is not obvious from its name. */
  help?: string;
}

export interface DataSourceFormData {
  code: string;
  name: string;
  type: string;
  property: string;
  label: string;
  sortOrder: string;
  isActive: boolean;
}

interface DataSourceModalProps {
  visible: boolean;
  title: string;
  fields: DataSourceFormField[];
  initialData?: Partial<DataSourceFormData>;
  /** Questions this datasource serves, for the `type` picker. Empty disables the select. */
  rowGroups?: DataSourceRowGroup[];
  onClose: () => void;
  onSave: (data: DataSourceFormData) => Promise<void>;
}

const defaultFormData: DataSourceFormData = {
  code: '',
  name: '',
  type: '',
  property: '',
  label: '',
  sortOrder: '',
  isActive: true,
};

const DataSourceModal: React.FC<DataSourceModalProps> = ({
  visible,
  title,
  fields,
  initialData,
  rowGroups = [],
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<DataSourceFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<DataSourceFormField['key'], string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setFormData({
        ...defaultFormData,
        ...initialData,
      });
      setErrors({});
      setSaveError(null);
    }
  }, [visible, initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors(prev => {
      if (!prev[name as DataSourceFormField['key']]) return prev;
      const next = { ...prev };
      delete next[name as DataSourceFormField['key']];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    // Required fields show their error under the field (FORM-01)
    const fieldErrors: Partial<Record<DataSourceFormField['key'], string>> = {};
    fields.forEach(field => {
      if (field.required && !String(formData[field.key] ?? '').trim()) {
        fieldErrors[field.key] = `${field.label} required`;
      }
    });
    const firstInvalid = fields.find(field => fieldErrors[field.key]);
    if (firstInvalid) {
      setErrors(fieldErrors);
      document.getElementById(`dataSource-${firstInvalid.key}`)?.focus();
      return;
    }

    try {
      setIsSaving(true);
      await onSave({
        code: (formData.code ?? '').trim(),
        name: (formData.name ?? '').trim(),
        type: (formData.type ?? '').trim(),
        property: (formData.property ?? '').trim(),
        label: (formData.label ?? '').trim(),
        sortOrder: String(formData.sortOrder ?? '').trim(),
        isActive: formData.isActive !== false,
      });
      onClose();
    } catch (error: unknown) {
      console.error('Error saving data source:', error);
      setSaveError(describeActionError(error, "Couldn't save your changes. Try again."));
    } finally {
      setIsSaving(false);
    }
  };

  const renderControl = (field: DataSourceFormField) => {
    const control = field.control ?? 'text';

    if (control === 'checkbox') {
      return (
        <Form.Check
          type="checkbox"
          id={`dataSource-${field.key}`}
          name={field.key}
          label={field.label}
          checked={Boolean(formData[field.key])}
          onChange={handleChange}
        />
      );
    }

    if (control === 'select') {
      // Distinct types only: CHRIS lists the same inventory type once per property.
      const options = [...new Set(rowGroups.map(group => group.type).filter(Boolean))] as string[];
      return (
        <Form.Select
          id={`dataSource-${field.key}`}
          name={field.key}
          value={formData[field.key] as string}
          onChange={handleChange}
          required={field.required}
          isInvalid={!!errors[field.key]}
        >
          <option value="">Select a question...</option>
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Select>
      );
    }

    return (
      <Form.Control
        type={control === 'number' ? 'number' : 'text'}
        {...(control === 'number' ? { min: 0, step: 1 } : {})}
        id={`dataSource-${field.key}`}
        name={field.key}
        value={formData[field.key] as string}
        onChange={handleChange}
        required={field.required}
        isInvalid={!!errors[field.key]}
      />
    );
  };

  return (
    <Modal show={visible} onHide={onClose} backdrop={isSaving ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      {/* Form wraps Body+Footer (footer holds the submit) and is the flex column itself. */}
      <form onSubmit={handleSubmit} noValidate className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          {saveError && (
            <Alert variant="danger" role="alert" className="tw-mb-3">
              {saveError}
            </Alert>
          )}
          {fields.map(field => (
            <div className="tw-mb-4" key={field.key}>
              {/* A checkbox renders its own label beside the box. */}
              {(field.control ?? 'text') !== 'checkbox' && (
                <Form.Label htmlFor={`dataSource-${field.key}`}>{field.label}</Form.Label>
              )}
              {renderControl(field)}
              {errors[field.key] && <Form.Control.Feedback type="invalid">{errors[field.key]}</Form.Control.Feedback>}
              {field.help && <Form.Text>{field.help}</Form.Text>}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default DataSourceModal;
